import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    clearNetworkLicense,
    createNetworkLicenseRenewalRequest,
    extendNetworkLicense,
    fetchLicenseRenewalRequests,
    fetchLicenseStatus,
    fetchNetworkLicenseStatus,
    fetchNetworksLicenseOverview,
    resolveLicenseRenewalRequest,
    setLicenseToken,
    setNetworkLicenseToken,
} from "../services/license";
import { fetchNetworks } from "../services/networks";

function tomorrowLocalYmd() {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function formatUtc(value) {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString("uk-UA", { timeZone: "UTC" }) + " UTC";
    } catch (_) {
        return String(value);
    }
}

function licenseTone(license) {
    if (!license) return "unknown";
    if (license.valid !== true) return "bad";
    if (Number.isFinite(license.days_left) && license.days_left <= 15) return "warn";
    return "ok";
}

function LicenseStatusChips({ license }) {
    const tone = licenseTone(license);
    return (
        <div className="license-status-chips">
            <span className={`license-chip is-${tone}`}>
                {tone === "ok" ? "Активна" : tone === "warn" ? "Скоро закінчиться" : tone === "bad" ? "Неактивна" : "Невідомо"}
            </span>
            {Number.isFinite(license?.days_left) ? (
                <span className="license-chip is-muted">
                    {license.days_left === 0 ? "Сьогодні останній день" : `Днів лишилось: ${license.days_left}`}
                </span>
            ) : null}
        </div>
    );
}

const LicensePage = ({ isSuperAdmin = false }) => {
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [overview, setOverview] = useState([]);
    const [renewalRequests, setRenewalRequests] = useState([]);
    const [requestFilter, setRequestFilter] = useState("pending");

    // Superadmin manage panel (existing)
    const [networks, setNetworks] = useState([]);
    const [selected, setSelected] = useState("global");
    const [status, setStatus] = useState(null);
    const [token, setToken] = useState("");
    const [extendOpen, setExtendOpen] = useState(false);
    const [extendDate, setExtendDate] = useState("");

    // Renewal form per network
    const [requestNetworkId, setRequestNetworkId] = useState("");
    const [requestComment, setRequestComment] = useState("");
    const [resolveNotes, setResolveNotes] = useState({});

    const loadOverview = useCallback(async () => {
        const data = await fetchNetworksLicenseOverview();
        const list = Array.isArray(data?.networks) ? data.networks : [];
        setOverview(list);
        if (!requestNetworkId && list.length) {
            setRequestNetworkId(String(list[0].network_id));
        }
    }, [requestNetworkId]);

    const loadRequests = useCallback(async () => {
        const list = await fetchLicenseRenewalRequests(requestFilter);
        setRenewalRequests(Array.isArray(list) ? list : []);
    }, [requestFilter]);

    const loadStatus = useCallback(async () => {
        if (!isSuperAdmin) return;
        const nextStatus = selected === "global"
            ? await fetchLicenseStatus()
            : await fetchNetworkLicenseStatus(Number(selected));
        setStatus(nextStatus);
    }, [isSuperAdmin, selected]);

    const refreshAll = useCallback(async () => {
        setLoading(true);
        setMessage("");
        try {
            await loadOverview();
            await loadRequests();
            if (isSuperAdmin) {
                try {
                    const list = await fetchNetworks();
                    setNetworks(Array.isArray(list) ? list : []);
                } catch (_) {
                    setNetworks([]);
                }
                await loadStatus();
            }
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [isSuperAdmin, loadOverview, loadRequests, loadStatus]);

    useEffect(() => {
        refreshAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadRequests().catch((error) => setMessage(`❌ ${error.message}`));
    }, [loadRequests]);

    useEffect(() => {
        if (!isSuperAdmin) return;
        setExtendOpen(false);
        setExtendDate("");
        loadStatus()
            .catch((error) => {
                setStatus(null);
                setMessage(`❌ ${error.message}`);
            });
    }, [isSuperAdmin, selected, loadStatus]);

    const pendingCount = useMemo(
        () => overview.filter((item) => item?.pending_request).length,
        [overview]
    );

    const onSendRequest = async () => {
        const networkId = Number(requestNetworkId);
        if (!Number.isFinite(networkId) || networkId <= 0) {
            setMessage("❌ Оберіть мережу");
            return;
        }
        const comment = String(requestComment || "").trim();
        if (comment.length < 3) {
            setMessage("❌ Додайте коментар (мін. 3 символи)");
            return;
        }
        setLoading(true);
        setMessage("");
        try {
            await createNetworkLicenseRenewalRequest(networkId, comment);
            setRequestComment("");
            setMessage("✅ Запит надіслано суперадміну");
            await loadOverview();
            await loadRequests();
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const onResolve = async (requestId, statusValue) => {
        setLoading(true);
        setMessage("");
        try {
            await resolveLicenseRenewalRequest(
                requestId,
                statusValue,
                resolveNotes[requestId] || ""
            );
            setMessage(statusValue === "done" ? "✅ Запит позначено виконаним" : "✅ Запит відхилено");
            await loadOverview();
            await loadRequests();
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const onSave = async () => {
        setLoading(true);
        setMessage("");
        try {
            const saved = selected === "global"
                ? await setLicenseToken(token)
                : await setNetworkLicenseToken(Number(selected), token);
            setStatus(saved);
            setToken("");
            setMessage("✅ Ліцензію збережено");
            await loadOverview();
            await loadRequests();
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const onClear = async () => {
        if (selected === "global") return;
        setLoading(true);
        setMessage("");
        try {
            await clearNetworkLicense(Number(selected));
            setStatus(null);
            setMessage("✅ Ліцензію мережі очищено");
            await loadStatus();
            await loadOverview();
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const onOpenExtend = () => {
        setMessage("");
        setExtendDate(tomorrowLocalYmd());
        setExtendOpen(true);
    };

    const onConfirmExtend = async () => {
        if (selected === "global") return;
        const trimmed = String(extendDate || "").trim();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            setMessage("❌ Оберіть дату в календарі");
            return;
        }
        if (trimmed < tomorrowLocalYmd()) {
            setMessage("❌ Дата продовження має бути в майбутньому");
            return;
        }

        setLoading(true);
        setMessage("");
        try {
            const next = await extendNetworkLicense(
                Number(selected),
                `${trimmed}T23:59:59Z`,
            );
            setStatus(next);
            setExtendOpen(false);
            setExtendDate("");
            setMessage("✅ Ліцензію мережі продовжено");
            await loadOverview();
            await loadRequests();
        } catch (error) {
            setMessage(`❌ ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const valid = status?.valid === true;
    const canExtendNetwork =
        isSuperAdmin
        && selected !== "global"
        && Boolean(status?.expires_at_utc || status?.expires_override_at_utc || status?.customer);

    const selectedNetworkMeta = useMemo(() => {
        if (selected === "global") return null;
        return networks.find((item) => String(item.id) === String(selected)) || null;
    }, [networks, selected]);

    return (
        <div className="license-page">
            <div className="section license-section">
                <div className="license-section__head">
                    <div>
                        <h3 style={{ margin: 0 }}>Ліцензії моїх мереж</h3>
                        <p className="license-section__hint">
                            Тут видно термін дії ліцензії по кожній мережі.
                            {!isSuperAdmin
                                ? " Щоб продовжити — надішліть запит суперадміну з коментарем."
                                : " Адміни мереж також можуть надсилати запити на продовження."}
                        </p>
                    </div>
                    <button type="button" className="action-button" onClick={refreshAll} disabled={loading}>
                        🔄 Оновити
                    </button>
                </div>

                {overview.length === 0 ? (
                    <div className="license-empty">Мереж ще немає.</div>
                ) : (
                    <div className="license-overview-grid">
                        {overview.map((item) => {
                            const lic = item.license || {};
                            const tone = licenseTone(lic);
                            return (
                                <div key={item.network_id} className={`license-overview-card is-${tone}`}>
                                    <div className="license-overview-card__title">
                                        <strong>{item.network_name}</strong>
                                        <span className="license-overview-card__id">#{item.network_id}</span>
                                    </div>
                                    <div className="license-overview-card__meta">
                                        {item.deployment_mode === "self_hosted" ? "Self-hosted" : "SaaS"}
                                        {item.mobile_apps === "provider" ? " · мобільний план" : " · без мобільного"}
                                    </div>
                                    <LicenseStatusChips license={lic} />
                                    <div className="license-overview-card__rows">
                                        <div><span>Діє до:</span> <b>{formatUtc(lic.expires_at_utc)}</b></div>
                                        {lic.customer ? <div><span>Клієнт:</span> <b>{lic.customer}</b></div> : null}
                                        {lic.detail ? <div className="license-overview-card__detail">{lic.detail}</div> : null}
                                    </div>
                                    {item.pending_request ? (
                                        <div className="license-pending-note">
                                            Відкритий запит від {formatUtc(item.pending_request.created_at_utc)}:
                                            {" "}«{item.pending_request.comment}»
                                        </div>
                                    ) : null}
                                    {!isSuperAdmin ? (
                                        <button
                                            type="button"
                                            className="action-button"
                                            disabled={loading || Boolean(item.pending_request)}
                                            onClick={() => {
                                                setRequestNetworkId(String(item.network_id));
                                                setRequestComment(
                                                    lic.valid
                                                        ? `Прошу продовжити ліцензію мережі «${item.network_name}» (залишилось ${lic.days_left ?? "?"} днів).`
                                                        : `Прошу видати / відновити ліцензію мережі «${item.network_name}».`
                                                );
                                            }}
                                        >
                                            {item.pending_request ? "Запит уже надіслано" : "Заповнити запит"}
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="action-button"
                                            onClick={() => setSelected(String(item.network_id))}
                                            disabled={loading}
                                        >
                                            Керувати токеном
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="section license-section">
                <h3 style={{ marginTop: 0 }}>Запит на продовження</h3>
                <p className="license-section__hint">
                    Запит побачить суперадмін провайдера. Один відкритий запит на мережу.
                    {pendingCount ? ` Зараз відкритих: ${pendingCount}.` : ""}
                </p>
                <div className="license-request-form">
                    <label>
                        Мережа
                        <select
                            value={requestNetworkId}
                            onChange={(e) => setRequestNetworkId(e.target.value)}
                            disabled={loading || !overview.length}
                        >
                            {!overview.length ? <option value="">Немає мереж</option> : null}
                            {overview.map((item) => (
                                <option key={item.network_id} value={String(item.network_id)}>
                                    #{item.network_id}: {item.network_name}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label>
                        Коментар для суперадміна
                        <textarea
                            rows={3}
                            value={requestComment}
                            onChange={(e) => setRequestComment(e.target.value)}
                            placeholder="Наприклад: потрібне продовження на 12 місяців, рахунок вже оплачено…"
                            disabled={loading}
                            maxLength={2000}
                        />
                    </label>
                    <button
                        type="button"
                        className="action-button"
                        onClick={onSendRequest}
                        disabled={loading || !overview.length}
                    >
                        📨 Надіслати запит
                    </button>
                </div>
            </div>

            <div className="section license-section">
                <div className="license-section__head">
                    <h3 style={{ margin: 0 }}>
                        {isSuperAdmin ? "Запити від адміністраторів" : "Мої запити"}
                    </h3>
                    <select
                        value={requestFilter}
                        onChange={(e) => setRequestFilter(e.target.value)}
                        disabled={loading}
                    >
                        <option value="pending">Відкриті</option>
                        <option value="done">Виконані</option>
                        <option value="rejected">Відхилені</option>
                        <option value="all">Усі</option>
                    </select>
                </div>

                {renewalRequests.length === 0 ? (
                    <div className="license-empty">Записів немає.</div>
                ) : (
                    <div className="license-requests-list">
                        {renewalRequests.map((req) => (
                            <div key={req.id} className={`license-request-card is-${req.status}`}>
                                <div className="license-request-card__top">
                                    <strong>
                                        #{req.network_id} {req.network_name || "Мережа"}
                                    </strong>
                                    <span className="license-chip is-muted">{req.status}</span>
                                </div>
                                <div className="license-request-card__meta">
                                    {req.requested_by_email ? <span>{req.requested_by_email}</span> : null}
                                    <span>{formatUtc(req.created_at_utc)}</span>
                                    {Number.isFinite(req.days_left_at_request) ? (
                                        <span>Було днів: {req.days_left_at_request}</span>
                                    ) : null}
                                </div>
                                <div className="license-request-card__comment">{req.comment}</div>
                                {req.resolver_note ? (
                                    <div className="license-request-card__note">
                                        Відповідь: {req.resolver_note}
                                    </div>
                                ) : null}
                                {isSuperAdmin && req.status === "pending" ? (
                                    <div className="license-request-card__actions">
                                        <input
                                            type="text"
                                            placeholder="Нотатка (опційно)"
                                            value={resolveNotes[req.id] || ""}
                                            onChange={(e) => setResolveNotes((prev) => ({
                                                ...prev,
                                                [req.id]: e.target.value,
                                            }))}
                                            disabled={loading}
                                        />
                                        <button
                                            type="button"
                                            className="action-button"
                                            disabled={loading}
                                            onClick={() => {
                                                setSelected(String(req.network_id));
                                                onResolve(req.id, "done");
                                            }}
                                        >
                                            Позначити виконаним
                                        </button>
                                        <button
                                            type="button"
                                            className="action-button"
                                            disabled={loading}
                                            onClick={() => onResolve(req.id, "rejected")}
                                        >
                                            Відхилити
                                        </button>
                                        <button
                                            type="button"
                                            className="action-button"
                                            disabled={loading}
                                            onClick={() => setSelected(String(req.network_id))}
                                        >
                                            Відкрити мережу ↓
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {isSuperAdmin ? (
                <div className="section license-section">
                    <h3 style={{ marginTop: 0 }}>Керування токенами (суперадмін)</h3>
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
                        <div><strong>Режим:</strong></div>
                        <select
                            value={selected}
                            onChange={(e) => setSelected(e.target.value)}
                            style={{ padding: 8, borderRadius: 8, border: "1px solid rgba(0,0,0,0.15)" }}
                            disabled={loading}
                        >
                            <option value="global">Глобальна (для бекенду)</option>
                            {networks.map((network) => {
                                const mode = network.deployment_mode === "self_hosted" ? "self-hosted" : "SaaS";
                                const mobile = network.mobile_apps === "provider" ? "мобільний план" : "без мобільного";
                                return (
                                    <option key={network.id} value={String(network.id)}>
                                        #{network.id}: {network.network_name} · {mode} · {mobile}
                                    </option>
                                );
                            })}
                        </select>
                        {selected !== "global" && (
                            <div style={{ fontSize: 12, opacity: 0.85 }}>
                                Для оператора і клієнта ліцензія перевіряється по network_id.
                            </div>
                        )}
                    </div>

                    {selected !== "global" && selectedNetworkMeta ? (
                        <div className="license-network-passport">
                            <strong>{selectedNetworkMeta.network_name}</strong>
                            <span>
                                {selectedNetworkMeta.deployment_mode === "self_hosted"
                                    ? "Self-hosted (дані у клієнта)"
                                    : "SaaS (дані у провайдера)"}
                            </span>
                            {selectedNetworkMeta.self_hosted_notes ? (
                                <div>Нотатки: {selectedNetworkMeta.self_hosted_notes}</div>
                            ) : null}
                        </div>
                    ) : null}

                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                        <div><strong>Статус:</strong> {valid ? "✅ активна" : "❌ неактивна"}</div>
                        {typeof status?.network_id === "number" && (
                            <div><strong>Network ID:</strong> {status.network_id}</div>
                        )}
                        {status?.expires_at_utc && (
                            <div><strong>Діє до (UTC):</strong> {status.expires_at_utc}</div>
                        )}
                        {status?.expires_override_at_utc && (
                            <div><strong>Продовжено до (UTC):</strong> {status.expires_override_at_utc}</div>
                        )}
                        {typeof status?.days_left === "number" && (
                            <div><strong>Днів лишилось:</strong> {status.days_left}</div>
                        )}
                        {status?.customer && (
                            <div><strong>Клієнт:</strong> {status.customer}</div>
                        )}
                    </div>

                    {status?.detail && (
                        <div style={{ marginTop: 8, opacity: 0.85 }}>{status.detail}</div>
                    )}

                    <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>
                            Вставте ліцензійний код формату LIC1....
                        </div>
                        <textarea
                            value={token}
                            onChange={(e) => setToken(e.target.value)}
                            placeholder="LIC1...."
                            rows={4}
                            style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid rgba(0,0,0,0.15)" }}
                        />
                    </div>

                    <div style={{ display: "flex", gap: 10, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <button className="action-button" onClick={onSave} disabled={loading || !token.trim()}>
                            {loading ? "⏳" : "💾"} Зберегти ліцензію
                        </button>
                        <button className="action-button" onClick={loadStatus} disabled={loading}>
                            🔄 Оновити статус
                        </button>
                        {canExtendNetwork && (
                            <button className="action-button" onClick={onOpenExtend} disabled={loading}>
                                ⏳ Продовжити без нового LIC1
                            </button>
                        )}
                        {selected !== "global" && (
                            <button className="action-button" onClick={onClear} disabled={loading}>
                                🧹 Очистити ліцензію мережі
                            </button>
                        )}
                    </div>

                    {extendOpen && canExtendNetwork && (
                        <div className="license-extend-box">
                            <strong>Продовжити ліцензію мережі</strong>
                            <div style={{ fontSize: 13, opacity: 0.85 }}>
                                Без нового LIC1. Дата діє як локальний override
                                {status?.expires_at_utc ? ` (зараз ефективно до ${status.expires_at_utc})` : ""}.
                            </div>
                            <label style={{ display: "grid", gap: 6 }}>
                                <span>Нова дата закінчення</span>
                                <input
                                    type="date"
                                    value={extendDate}
                                    min={tomorrowLocalYmd()}
                                    onChange={(e) => setExtendDate(e.target.value)}
                                    disabled={loading}
                                />
                            </label>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <button
                                    className="action-button"
                                    type="button"
                                    onClick={onConfirmExtend}
                                    disabled={loading || !extendDate}
                                >
                                    Підтвердити
                                </button>
                                <button
                                    className="action-button"
                                    type="button"
                                    onClick={() => {
                                        setExtendOpen(false);
                                        setExtendDate("");
                                    }}
                                    disabled={loading}
                                >
                                    Скасувати
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : null}

            {message ? <div className="license-page-message">{message}</div> : null}
        </div>
    );
};

export default LicensePage;
